import http from '@clients/http.client';
import { runCordovaPluginCall } from '@helpers/cordovaBridge';
import './browserAttribution';

interface IBrowserAttribution {
  getTrackingId(): Promise<string | undefined>;
  destination(url: string): Promise<string>;
  decorate(url: string): string;
}
const browser = () => (window as any).__adAttribution as IBrowserAttribution;
let reading: Promise<string | undefined> | undefined;
let nativeId: string | undefined;
let nativeRead = false;

export function getAttributionTrackingId(): Promise<string | undefined> {
  const cordova = (window as any).cordova;
  if (!cordova) {
    try { return browser().getTrackingId().catch(() => undefined); }
    catch (_) { return Promise.resolve(undefined); }
  }
  if (cordova.platformId !== 'android') { return Promise.resolve(undefined); }
  if (nativeRead) { return Promise.resolve(nativeId); }
  if (reading) { return reading; }
  reading = new Promise<string | undefined>(resolve => {
    let finished = false;
    const finish = (value?: string, success = false) => {
      if (finished) { return; }
      finished = true;
      clearTimeout(timer);
      if (success) {
        nativeRead = true;
        nativeId = value && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value) ? value : undefined;
      }
      resolve(nativeId);
    };
    const timer = setTimeout(() => finish(), 3000);
    try {
      const started = runCordovaPluginCall('VVInstallReferrer.getTrackingId', () => {
        cordova.exec((value: string) => finish(value, true), () => finish(), 'VVInstallReferrer', 'getTrackingId', []);
        return true;
      }, false);
      if (!started) { finish(); }
    } catch (_) { finish(); }
  }).then(value => { reading = undefined; return value; });
  return reading;
}

const CLAIMED_KEY = 'coparenting.ad-attribution.claimed.v1';
let claiming: Promise<void> | undefined;

const alreadyClaimed = (trackingId: string) => {
  try { return localStorage.getItem(CLAIMED_KEY) === trackingId; } catch (_) { return false; }
};
const rememberClaimed = (trackingId: string) => {
  try { localStorage.setItem(CLAIMED_KEY, trackingId); } catch (_) { /* Retry next session. */ }
};

// Called after authentication and on account refreshes. A settled ID (claimed, or
// rejected as invalid/expired/owned by another account) is not sent again; network
// and server errors keep the ID for a later retry. Never fails login.
export function claimAttribution(authorization: string): Promise<void> {
  if (!authorization) { return Promise.resolve(); }
  if (claiming) { return claiming; }
  claiming = (async () => {
    try {
      const trackingId = await getAttributionTrackingId();
      if (!trackingId || alreadyClaimed(trackingId)) { return; }
      try {
        await http.post('https://api.varannanvecka.app/v1/attribution/claim', { trackingId }, {
          headers: { Authorization: `Bearer ${authorization.replace(/^Bearer /, '')}` },
          timeout: 2500,
        });
        rememberClaimed(trackingId);
      } catch (error) {
        const status = error && (error as any).response && (error as any).response.status;
        if (status === 400 || status === 404) { rememberClaimed(trackingId); }
      }
    } catch (_) { /* Retry on the next account refresh/device update. */ }
  })().then(() => { claiming = undefined; });
  return claiming;
}

export function attributedStoreUrl(url: string): string {
  try { return browser().decorate(url); } catch (_) { return url; }
}
export async function prepareStoreUrl(url: string): Promise<string> {
  try { return await browser().destination(url); } catch (_) { return url; }
}



// WEBPACK FOOTER //
// ./src/services/attribution/attribution.ts